import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material'; // Удаляем Box, CircularProgress, Alert
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthPage } from './pages/AuthPage';
import { DashboardPage } from './pages/DashboardPage';
import { ManagerDashboardPage } from './pages/dashboard/ManagerDashboardPage';
import OrganizationDetailsPage from './pages/OrganizationDetailsPage';
import TaskDetailsPage from './pages/TaskDetailsPage';
import TaskDraftDetailsPage from './pages/TaskDraftDetailsPage'; // Импортируем TaskDraftDetailsPage
import AdminDashboardPage from './pages/AdminDashboardPage'; // Импортируем AdminDashboardPage
import ExecutorDashboardPage from './pages/executor/dashboard/desktop/ExecutorDashboardPage'; // Импортируем ExecutorDashboardPage
import OrganizationsListPage from './pages/admin/OrganizationsListPage'; // Импортируем OrganizationsListPage
import AdminOrganizationTasksPage from './pages/admin/AdminOrganizationTasksPage'; // Импортируем AdminOrganizationTasksPage
import { useAuth } from './contexts/AuthContext'; // Импортируем useAuth
import { UserType } from './types/auth'; // Удаляем ManagerRole, так как он не используется здесь напрямую
import ExecutorTaskDetailsPage from './pages/executor/task-details/desktop/ExecutorTaskDetailsPage'; // Импортируем ExecutorTaskDetailsPage
import OrganizationManagerDashboardPage from './pages/organization-manager/dashboard/desktop/OrganizationManagerDashboardPage'; // Импортируем OrganizationManagerDashboardPage
import AdminTaskDetailsPage from './pages/admin/AdminTaskDetailsPage'; // Импортируем AdminTaskDetailsPage
import CreateTaskBriefPage from './pages/organization-manager/tasks/create/CreateTaskBriefPage'; // Импортируем CreateTaskBriefPage
import AdminTaskEntityDetailsPage from './pages/admin/AdminTaskEntityDetailsPage'; // Добавлено

// Создаем тему Material-UI
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
      light: '#8fa4f3',
      dark: '#5a6fd8',
    },
    secondary: {
      main: '#764ba2',
      light: '#9575cd',
      dark: '#512da8',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          '&:hover': {
            boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AuthRedirectHandler /> {/* Новый компонент для обработки редиректа */}
          <Routes>
            {/* AuthRedirectHandler теперь обрабатывает перенаправление с корневого пути */}
            <Route path="/" element={<React.Fragment />} /> 
            
            {/* Страница авторизации */}
            <Route 
              path="/auth" 
              element={<AuthPage />} 
            />
            {/* Страница авторизации для админов/менеджеров с предустановленным шагом */}
            <Route 
              path="/admin/login" 
              element={<AuthPage initialStep="manager" />} 
            />
            
            
            {/* Защищенные маршруты */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } 
            />

            {/* Страница менеджера */}
            <Route
              path="/manager-dashboard"
              element={
                <ProtectedRoute requiredRole={['manager']}>
                  <ManagerDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Страница деталей компании (для менеджеров) */}
            <Route
              path="/organization/:id"
              element={
                <ProtectedRoute requiredRole={['manager']}>
                  <OrganizationDetailsPage />
                </ProtectedRoute>
              }
            />

            {/* Страница деталей задачи (для менеджеров) */}
            <Route
              path="/task/:id"
              element={
                <ProtectedRoute requiredRole={['manager']}>
                  <TaskDetailsPage />
                </ProtectedRoute>
              }
            />

            {/* Страница деталей черновика задачи (для менеджеров организаций и кураторов платформы) */}
            <Route
              path="/task-draft/:id"
              element={
                <ProtectedRoute requiredRole={['OrganizationManager', 'PlatformCurator']}>
                  <TaskDraftDetailsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Страница создания брифа задачи (для менеджеров организаций) */}
            <Route
              path="/organization-manager/tasks/create"
              element={
                <ProtectedRoute requiredRole={['OrganizationManager']}>
                  <CreateTaskBriefPage />
                </ProtectedRoute>
              }
            />

            {/* Дашборд исполнителя */}
            <Route
              path="/executor/dashboard"
              element={
                <ProtectedRoute requiredRole={['user']}>
                  <ExecutorDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Дашборд менеджера организации */}
            <Route
              path="/organization-manager/dashboard"
              element={
                <ProtectedRoute requiredRole={['OrganizationManager']}>
                  <OrganizationManagerDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Страница деталей задачи для исполнителя */}
            <Route
              path="/executor/task/:id"
              element={
                <ProtectedRoute requiredRole={['user']}>
                  <ExecutorTaskDetailsPage />
                </ProtectedRoute>
              }
            />

            {/* Страницы задач (для всех авторизованных пользователей) */}
            <Route 
              path="/tasks" 
              element={
                <ProtectedRoute>
                  <div>Страница задач в разработке...</div>
                </ProtectedRoute>
              } 
            />
            
            {/* Страница пользователей (только для админов и кураторов) */}
            <Route 
              path="/users" 
              element={
                <ProtectedRoute requiredRole={['admin', 'curator']}>
                  <div>Страница пользователей в разработке...</div>
                </ProtectedRoute>
              } 
            />
            
            {/* Профиль */}
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <div>Страница профиля в разработке...</div>
                </ProtectedRoute>
              } 
            />
            
            {/* Настройки */}
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <div>Страница настроек в разработке...</div>
                </ProtectedRoute>
              } 
            />

            {/* Страница админ-дашборда */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requiredRole={['PlatformCurator']}> {/* Изменено: только PlatformCurator */}
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Страница списка организаций (для админов) */}
            <Route
              path="/admin/organizations"
              element={
                <ProtectedRoute requiredRole={['PlatformCurator']}>
                  <OrganizationsListPage />
                </ProtectedRoute>
              }
            />

            {/* Страница деталей организации для админа */}
            <Route
              path="/admin/organizations/:id"
              element={
                <ProtectedRoute requiredRole={['PlatformCurator']}>
                  <AdminOrganizationTasksPage /> {/* Используем AdminOrganizationTasksPage */}
                </ProtectedRoute>
              }
            />
            
            {/* Страница деталей задачи для админа */}
            <Route
              path="/admin/tasks/:id"
              element={
                <ProtectedRoute requiredRole={['PlatformCurator']}>
                  <AdminTaskDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/task-entity/:id"
              element={
                <ProtectedRoute requiredRole={['PlatformCurator']}>
                  <AdminTaskEntityDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/organizations/:organizationId/tasks/create-brief"
              element={
                <ProtectedRoute requiredRole={['PlatformCurator', 'Admin']}>
                  <CreateTaskBriefPage />
                </ProtectedRoute>
              }
            />
            
            {/* 404 - страница не найдена */}
            <Route 
              path="*" 
              element={
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  minHeight: '100vh',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  <h1>404 - Страница не найдена</h1>
                  <a href="/dashboard">Вернуться на главную</a>
                </div>
              } 
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

// Новый компонент для обработки логики редиректа после авторизации
const AuthRedirectHandler: React.FC = () => {
  const { state } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // Используем хук useLocation

  useEffect(() => {
    console.log('AuthRedirectHandler: useEffect triggered');
    console.log('AuthRedirectHandler: state.isLoading', state.isLoading);
    console.log('AuthRedirectHandler: state.user', state.user);
    console.log('AuthRedirectHandler: location.pathname', location.pathname);

    if (state.isLoading) {
      // Still loading, do nothing
      console.log('AuthRedirectHandler: Loading, returning null');
      return;
    }
    
    console.log('AuthRedirectHandler: Finished loading, proceeding with redirect logic.');

    if (state.user) {
      // User is authenticated
      let expectedDashboardPath: string;
      if (state.user.type === UserType.USER) {
        expectedDashboardPath = '/executor/dashboard';
      } else if (state.user.type === UserType.MANAGER) {
        if (state.user.manager?.roles?.includes('PlatformCurator')) {
          expectedDashboardPath = '/admin/dashboard';
        } else if (state.user.manager?.roles?.includes('OrganizationManager')) {
          expectedDashboardPath = '/organization-manager/dashboard';
        } else {
          // Fallback для менеджеров с неожиданными ролями или без конкретной роли дашборда
          expectedDashboardPath = '/dashboard';
        }
      } else {
        // Fallback для неожиданных типов пользователей
        expectedDashboardPath = '/dashboard';
      }

      // Check if user is on an incorrect dashboard path OR on root/auth page
      const currentPathIsDashboardBase = 
        location.pathname === '/dashboard' ||
        location.pathname === '/organization-manager/dashboard';

      const currentPathMatchesExpectedDashboard = location.pathname.startsWith(expectedDashboardPath);

      if (location.pathname === '/' || location.pathname === '/auth' || 
          (currentPathIsDashboardBase && !currentPathMatchesExpectedDashboard)) {
        
        console.log('AuthRedirectHandler: Authenticated user redirecting to', expectedDashboardPath, 'from', location.pathname);
        navigate(expectedDashboardPath, { replace: true });
      } else {
        console.log('AuthRedirectHandler: Authenticated user on correct path or sub-path, no redirect needed.');
      }

    } else {
      // User is not authenticated
      // Redirect to /auth if on root, /dashboard, /executor/dashboard, or /manager-dashboard
      const needsAuthRedirect = 
        location.pathname === '/' ||
        location.pathname === '/dashboard' ||
        location.pathname === '/executor/dashboard' ||
        location.pathname === '/organization-manager/dashboard';

      if (needsAuthRedirect) {
        console.log('AuthRedirectHandler: Unauthenticated user, redirecting to /auth from', location.pathname);
        navigate('/auth', { replace: true });
      } else {
        console.log('AuthRedirectHandler: Unauthenticated user on non-protected path, no redirect needed.');
      }
    }
  }, [state.isLoading, state.user, navigate, location.pathname]);

  return null; // Этот компонент ничего не рендерит
};